import type { PointerEventHandler } from "react";
import { FiTrash2, FiEdit2 } from "react-icons/fi";
import { CardActionMenu } from "@/components/ui/CardActionMenu";

type BudgetCardStatus = "over" | "near" | "safe";

interface BudgetCardProps {
  name: string;
  spent: string;
  amountConnector: string;
  amount: string;
  percent: number;
  percentLabel: string;
  detailAmount: string;
  detailLabel: string;
  status: BudgetCardStatus;
  statusLabel: string;
  editLabel: string;
  deleteLabel: string;
  progressLabel: string;
  progressValueText: string;
  canEdit: boolean;
  isSwiped: boolean;
  menuLabel: string;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onPointerDown?: PointerEventHandler<HTMLButtonElement>;
  onPointerMove?: PointerEventHandler<HTMLButtonElement>;
  onPointerUp?: PointerEventHandler<HTMLButtonElement>;
  onPointerLeave?: PointerEventHandler<HTMLButtonElement>;
  onPointerCancel?: PointerEventHandler<HTMLButtonElement>;
}

const statusClass: Record<BudgetCardStatus, string> = {
  over: "bg-[var(--ds-card-flame)] [--budget-track:var(--ds-track-flame)]",
  near: "bg-[var(--ds-card-sun)] [--budget-track:var(--ds-track-sun)]",
  safe: "bg-[var(--ds-card-mist)] [--budget-track:var(--ds-track-mist)]",
};

export const BudgetCard = ({
  name,
  spent,
  amountConnector,
  amount,
  percent,
  percentLabel,
  detailAmount,
  detailLabel,
  status,
  statusLabel,
  editLabel,
  deleteLabel,
  progressLabel,
  progressValueText,
  canEdit,
  isSwiped,
  menuLabel,
  onClick,
  onEdit,
  onDelete,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
}: BudgetCardProps) => {
  const clampedPercent = Math.min(100, Math.max(0, percent));

  return (
    <div className="relative max-w-full self-start" data-swiped={isSwiped || undefined}>
      <div className="relative overflow-hidden rounded-[var(--ds-radius-lg)]">
        <div className="absolute inset-y-0 right-0 flex">
          <button type="button" onClick={onEdit} disabled={!canEdit} aria-label={`${editLabel} ${name}`} tabIndex={isSwiped ? 0 : -1} className="inline-flex min-h-11 w-20 items-center justify-center bg-[var(--ds-on-card)] text-[var(--ds-ink)] disabled:cursor-not-allowed disabled:opacity-50">
            <FiEdit2 aria-hidden="true" />
          </button>
          <button type="button" onClick={onDelete} aria-label={`${deleteLabel} ${name}`} tabIndex={isSwiped ? 0 : -1} className="inline-flex min-h-11 w-20 items-center justify-center rounded-r-[var(--ds-radius-lg)] bg-[var(--ds-card-flame)] text-[var(--ds-on-card)]">
            <FiTrash2 aria-hidden="true" />
          </button>
        </div>
        <article className={`relative h-full rounded-[var(--ds-radius-lg)] p-5 text-[var(--ds-on-card)] transition-[filter,transform] duration-150 hover:brightness-95 sm:p-6 ${statusClass[status]}`} style={{ transform: `translateX(-${isSwiped ? 10 : 0}rem)` }}>
          <button
            type="button"
            onClick={onClick}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerLeave}
            onPointerCancel={onPointerCancel}
            className="app-focus-ring block w-full min-w-0 rounded-[var(--ds-radius-md)] text-left"
          >
            <span className="flex flex-wrap items-start justify-between gap-3 pr-10">
              <span className="min-w-[9rem] flex-1 basis-32">
                <span className="block break-words text-xl font-medium leading-7 sm:text-[22px]">{name}</span>
                <span className="mt-0.5 block text-[15px] leading-[22px]">{statusLabel}</span>
              </span>
              <span className="shrink-0 text-right whitespace-nowrap">
                <span className="font-[var(--ds-font-display)] text-[28px] leading-9 tracking-[-0.03em] sm:text-[34px]">{detailAmount}</span>
                <span className="ml-1 text-sm font-medium leading-5">{detailLabel}</span>
              </span>
            </span>
            <span className="mt-2 block text-[15px] leading-5">{spent} {amountConnector} {amount}</span>
            <span className="mt-3 flex items-center gap-3">
              <span role="progressbar" aria-label={progressLabel} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clampedPercent)} aria-valuetext={progressValueText} className="block h-3 min-w-0 flex-1 overflow-hidden rounded-full bg-[var(--budget-track)]">
                <span className="block h-full rounded-full bg-[var(--ds-on-card)]" style={{ width: `${clampedPercent}%` }} />
              </span>
              <span className="shrink-0 text-[15px] font-medium leading-5 tabular-nums">{percentLabel}</span>
            </span>
          </button>
        </article>
      </div>
      <div className="absolute right-4 top-4 z-20 sm:right-5 sm:top-5">
        <CardActionMenu
          name={name}
          menuLabel={menuLabel}
          actions={[
            { kind: "edit", label: editLabel, onClick: onEdit, disabled: !canEdit },
            { kind: "delete", label: deleteLabel, onClick: onDelete },
          ]}
        />
      </div>
    </div>
  );
};
